/**
 * Funções de avaliação posicional e tática para a Inteligência Artificial.
 *
 * Avalia ataques, curas, valor de terreno e exposição a ameaças com base nas
 * regras do @oz/rules.
 */
import { forecast } from '../combat.js';
import { terrainAt } from '../grid.js';
import { weaponOf } from '../state.js';
import { hasCondition } from '../states.js';
// Uma só, para a IA, o motor e a tela: ver `combatant.ts`.
export { combatantOf } from '../combatant.js';
export function comparePositions(a, b) {
    return a.y - b.y || a.x - b.x;
}
export function scoreAttack(content, attacker, attackerUnit, defender, targetUnit, dist, difficulty) {
    const plan = forecast(content, attacker, defender, dist);
    if (!plan.attacker.canAttack)
        return null;
    const hitProb = plan.attacker.hit / 100;
    const critMultiplier = 1 + (plan.attacker.crit / 100) * 2;
    const expectedDamage = plan.attacker.damage * plan.attacker.attacks * hitProb * critMultiplier;
    let expectedRetaliation = 0;
    let isSuicidal = false;
    if (plan.defender.canAttack) {
        const defHitProb = plan.defender.hit / 100;
        const defCritMult = 1 + (plan.defender.crit / 100) * 2;
        expectedRetaliation = plan.defender.damage * plan.defender.attacks * defHitProb * defCritMult;
        if (plan.defender.damage * plan.defender.attacks >= attackerUnit.currentHp && plan.defender.hit > 40) {
            isSuicidal = true;
        }
    }
    // Eliminar o inimigo no primeiro confronto previne contra-ataque se for no primeiro golpe
    const firstBlowLethal = plan.attacker.damage >= targetUnit.currentHp && plan.attacker.hit >= 50;
    if (firstBlowLethal) {
        expectedRetaliation = 0;
        isSuicidal = false;
    }
    const isLethal = expectedDamage >= targetUnit.currentHp;
    // Iniciativa do atacante: agir e causar dano tem valor intrínseco positivo
    let score = 10 + expectedDamage;
    if (isLethal) {
        score += difficulty === 'dificil' ? 45 : 30;
    }
    if (targetUnit.isCommander) {
        score += difficulty === 'dificil' ? 35 : 20;
    }
    // Alvos frágeis ou de suporte (curandeiros)
    const targetWeapon = weaponOf(content, targetUnit);
    if (targetWeapon?.heals) {
        score += 15;
    }
    // Penalidade de contra-ataque ponderada pela dificuldade
    const retaliationWeight = difficulty === 'dificil' ? 1.0 : difficulty === 'normal' ? 0.75 : 0.4;
    score -= expectedRetaliation * retaliationWeight;
    if (isSuicidal) {
        score -= difficulty === 'dificil' ? 80 : difficulty === 'normal' ? 40 : 10;
    }
    // Vantagem de triângulo
    if (plan.attacker.triangle === 'vantagem') {
        score += 5;
    }
    else if (plan.attacker.triangle === 'desvantagem') {
        score -= 5;
    }
    // O que o golpe pode deixar no alvo vale na proporção da chance — e nada,
    // se o alvo já está com aquilo.
    for (const affliction of plan.attacker.inflicts ?? []) {
        if (hasCondition(targetUnit.states, affliction.condition))
            continue;
        score += (affliction.chance / 100) * hitProb * 6;
    }
    // Bônus de terreno do atacante
    score += (attacker.terrain?.defense || 0) * 2;
    score += (attacker.terrain?.avoid || 0) * 0.1;
    return {
        total: score,
        expectedDamage,
        expectedRetaliation,
        isLethal,
        isSuicidal,
    };
}
export function scoreHealing(targetUnit, healAmount, difficulty, 
/** Quantas condições do alvo o cajado remove. */
cures = 0) {
    const missingHp = targetUnit.stats.hp - targetUnit.currentHp;
    if (missingHp <= 0 && cures === 0)
        return -100;
    const effectiveHeal = Math.min(healAmount, Math.max(0, missingHp));
    const percentMissing = Math.max(0, missingHp) / targetUnit.stats.hp;
    // Tirar a paralisia de um aliado vale um turno inteiro dele — mais do que a
    // maior parte das curas de PV.
    let score = effectiveHeal * 2 + percentMissing * 25 + cures * 18;
    if (targetUnit.isCommander) {
        score += difficulty === 'dificil' ? 30 : 20;
    }
    return score;
}
export function scoreDefensivePosition(content, state, at, closestEnemyDistance, difficulty) {
    const terrain = terrainAt(state.map, content, at);
    let score = 0;
    // Valoriza terrenos que dão defesa e esquiva
    score += (terrain?.defense || 0) * 4;
    score += (terrain?.avoid || 0) * 0.2;
    if (terrain?.heal) {
        score += terrain.heal * 2;
    }
    // Em modo defensivo, não se afastar demais nem se expor sozinho
    if (difficulty === 'dificil') {
        if (closestEnemyDistance >= 2 && closestEnemyDistance <= 4) {
            score += 6; // Zona ideal para forçar o inimigo a vir
        }
    }
    return score;
}
//# sourceMappingURL=evaluator.js.map